import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';
import { captureVideoState } from '../video-state';
import { queryVideoState } from '../video-state/graphql/queryVideoState';
import { VIDEO_STATE_SCHEMA_SDL } from '../video-state/graphql/schema';
import { reportIfVideoStateOversized } from '../videoStateSizeGuard';

import { Tool } from './Tool';

export const getVideoStateSchema = z.object({
    query: z
        .string()
        .describe('A GraphQL query selecting the fields you need (see the tool description).'),
});
export type GetVideoStateArgs = z.infer<typeof getVideoStateSchema>;

const toolDescription = `Read the current video state with a GraphQL query, returning only the fields you ask for as JSON.

A query is REQUIRED — this tool no longer returns the whole video. You already receive the current state and its per-turn change diffs automatically, so query for a specific thing (one scene's assets, a group's narration, which scene mentions X) rather than re-reading everything. Ask for only the fields you need; the state is shallow (video → scene group → scene → asset), so queries should be too.

Scene \`id\` and scene-group \`id\` are the same short handles the editing tools accept, so you can query a node and act on it with the same id.

Schema:
${VIDEO_STATE_SCHEMA_SDL}

Examples:
- Orient: \`{ video { sceneCount duration sceneGroups { id type duration scenes { id duration covers } } } }\`
- One scene's assets: \`{ scene(id: "a1b2c3") { assets { name type text mediaId } } }\`
- Find scenes mentioning a word: \`{ video { sceneGroups(containsText: "pricing") { scenes { id covers } } } }\`
- A group's narration: \`{ video { sceneGroups(id: "aaaaaa") { transcript { text wordCount } } } }\`
- Everything about one asset: \`{ scene(id: "a1b2c3") { assets(type: "MEDIA") { properties } } }\``;

/**
 * Reads the current video state via a required GraphQL query. The model re-read the whole video far
 * more than it needed (AP-1256), so the tool no longer dumps everything: it answers a query over the
 * same captured snapshot the auto-injected state is rendered from, scoped to the agent's own scene
 * groups, and short-circuits a re-query whose result hasn't changed.
 */
export class GetVideoStateTool extends Tool<typeof getVideoStateSchema> {
    protected override readonly schema = getVideoStateSchema;
    protected override readonly toolName = TOOL_NAMES.getVideoState;
    public override readonly subagentAccess = SubagentAccess.READ_ONLY;
    protected override readonly toolDescription = toolDescription;

    public override getToolDisplayName(): string {
        return 'Reading video state';
    }

    public get statusIcon(): string {
        return 'movie_info';
    }

    public async execute(args: GetVideoStateArgs, context: ToolExecutionContext) {
        try {
            // Capture in the hosting agent's color-name mode so a queried value reads exactly like
            // the auto-injected video state (bare hex for the toolbox, hex + name for legacy).
            const includeColorNames = context.videoStateManager?.getIncludeColorNames() ?? true;
            const snapshot = await captureVideoState(
                this.luminary,
                undefined,
                this.getState,
                includeColorNames,
            );
            const result = await queryVideoState(snapshot, args.query, {
                // A parallel subagent only sees its own scene groups, matching the scoped full state.
                sceneGroupScope: context.subagentScope,
            });

            // The model re-reads state redundantly ~2 of every 3 times (AP-1256). When a query's
            // result is byte-identical to the last read, nothing it reads has changed since — return
            // a short notice instead of the same payload.
            const manager = context.videoStateManager;
            if (manager && manager.getLastStateReadResult() === result) {
                return (
                    `No change: this query returns the same result as your last ${TOOL_NAMES.getVideoState} ` +
                    'read — nothing it reads has changed since. Reuse that result rather than re-querying. ' +
                    'Read again only after a change (an edit tool, or a <video-state-modifications> update).'
                );
            }
            manager?.setLastStateReadResult(result);
            // A query can still ask for the whole video; flag an oversized result the same way.
            reportIfVideoStateOversized(
                result,
                context.model,
                TOOL_NAMES.getVideoState,
                context.task ?? 'unknown',
            );
            return result;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error getting video state: ${errorMessage}`;
        }
    }
}
